"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _SeatReservationAsset = _interopRequireDefault(require("./SeatReservationAsset.js"));
var _SeatReservationKey = _interopRequireDefault(require("./SeatReservationKey.js"));
var _EmptyStateGate = _interopRequireDefault(require("../Components/EmptyStateGate.js"));
var _Flex = _interopRequireDefault(require("../Components/Flex"));
var _String = _interopRequireDefault(require("../Components/String"));
var _SeatReservationTrainModule = _interopRequireDefault(require("./SeatReservationTrain.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Seat reservation UI component grouping multiple asset seat plans 
 * as a train for rendering to the user - the primary interaction surface
 * 
 * @package HOPS
 * @subpackage SeatReservation
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */class SeatReservationTrain extends _react.default.PureComponent {
  /**
   * Get the seat plan object for a train asset.
   *
   * @param {Object} trainAsset
   * @return {Object|Undefined}
   */
  getTrainAssetSeatPlan(trainAsset) {
    return this.props.seatPlans.find(sp => sp.Id === trainAsset?.Asset?.SeatPlan);
  }

  /**
   * Get the seat reservation objects made for a train asset.
   * 
   * @param {Object} trainAsset
   * @return {Array<Object>}
   */
  getTrainAssetSeatReservations(trainAsset) {
    const spaceIds = (this.getTrainAssetSeatPlan(trainAsset)?.Spaces || []).map(s => s?.Id);
    return this.props.seatReservations.filter(sr => {
      return spaceIds.includes(sr?.Space) && sr?.TrainAsset === trainAsset.Id;
    });
  }

  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  render() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
      width: "100%",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
        columnar: true,
        justifyContent: "space-between",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
          gap: 0.25,
          children: [this.props.showHeadcode && /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            noFlex: true,
            str: `Train ${this.props.train.Headcode}`,
            variant: "h6"
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            color: "textSecondary",
            noFlex: true,
            str: this.props.caption
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
          alignItems: "flex-end",
          flexShrink: 0,
          gap: 0.25,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            color: this.allocatedCount > this.maxAllocation ? "error" : this.allocationComplete ? "primary" : "textSecondary",
            noFlex: true,
            str: this.selectionLabel,
            textAlign: "right"
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            color: this.allocatedCount < this.props.requiredSelectedCount ? "error" : undefined,
            noFlex: true,
            str: this.selectionRequiredLabel,
            textAlign: "right",
            variant: "body2"
          })]
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_EmptyStateGate.default, {
        isEmpty: !this.props.assets?.length,
        emptyMessage: "(No seats from which to make a selection)",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          className: _SeatReservationTrainModule.default.assetsContainer,
          children: this.props.assets.map((asset, key) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_SeatReservationAsset.default, {
            activeSpaceSelection: this.props.selectedSpaces,
            asset: asset,
            assetLabel: `Carriage ${!(asset.Letter === null) && asset.Letter !== "" && typeof asset.Letter === "string" ? asset.Letter : key + 1} `,
            assetRemarks: asset.Remarks,
            containerLevel: this.props.containerLevel,
            maxSelectionExceeded: this.allocationComplete,
            onChangeSelection: this.props.onChangeSelection,
            seatPlan: this.getTrainAssetSeatPlan(asset),
            seatReservations: this.getTrainAssetSeatReservations(asset),
            targetBasketId: this.props.targetBasketId,
            targetOrderId: this.props.targetOrderId,
            train: this.props.train,
            trainScheduleIdDeparture: this.props.trainScheduleIdDeparture,
            trainScheduleIdArrival: this.props.trainScheduleIdArrival
          }, key))
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
          gap: 0,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
            color: "textSecondary",
            noFlex: true,
            str: "Key to Colours & Symbols"
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_SeatReservationKey.default, {
            seatPlans: this.props.seatPlans
          })]
        })]
      })]
    });
  }

  /**
   * Get whether the seat allocation is complete.
   *
   * @return {Boolean}
   */
  get allocationComplete() {
    return this.allocatedCount >= this.maxAllocation;
  }

  /**
   * Get the number of space allocations we've made.
   *
   * @return {Integer}
   */
  get allocatedCount() {
    return this.props.selectedSpaces.length;
  }

  /**
   * Get the maximum number of space allocations allowed to be made.
   *
   * @return {Integer|undefined}
   */
  get maxAllocation() {
    return this.props.maxSelectedCount;
  }

  /**
   * Get the current selection count label.
   * 
   * @return {String|undefined}
   */
  get selectionLabel() {
    if (this.allocatedCount > this.maxAllocation) {
      if (this.maxAllocation === 0) {
        return `Selection no longer applicable - please remove.`;
      } else return `${this.allocatedCount} seats selected, ${this.maxAllocation} allowed`;
    } else if (this.maxAllocation === 0) {
      return `Seat selection not applicable`;
    } else return `${this.allocatedCount} of ${this.maxAllocation} seats selected`; // Request from DS to always show numbers, even if single seat
  }

  /**
   * Get the "selection required" count label.
   *
   * @return {String|undefined}
   */
  get selectionRequiredLabel() {
    if (this.maxAllocation === 0) {
      return undefined;
    } else if (this.props.requiredSelectedCount === 0) {
      if (!(this.allocatedCount > this.maxAllocation)) {
        return "Selection is optional";
      } else return undefined;
    } else if (this.props.requiredSelectedCount === 1 && this.maxAllocation === 1) {
      return undefined;
    } else return `(${this.props.requiredSelectedCount} required)`;
  }
  static propTypes = {
    assets: _propTypes.default.arrayOf(_propTypes.default.object),
    caption: _propTypes.default.string,
    containerLevel: _propTypes.default.number,
    fabColor: _propTypes.default.oneOf(["primary", "secondary"]),
    index: _propTypes.default.number,
    journeyId: _propTypes.default.string,
    maxSelectedCount: _propTypes.default.number,
    onChangeSelection: _propTypes.default.func,
    requiredSelectedCount: _propTypes.default.number,
    seatPlans: _propTypes.default.arrayOf(_propTypes.default.object),
    seatReservations: _propTypes.default.array,
    selectedSpaces: _propTypes.default.array,
    showHeadcode: _propTypes.default.bool,
    train: _propTypes.default.object
  };
  static defaultProps = {
    containerLevel: 0,
    fabColor: "secondary",
    index: 0,
    requiredSelectedCount: 0,
    seatReservations: [],
    selectedSpaces: [],
    showHeadcode: true
  };
}
var _default = exports.default = SeatReservationTrain;